import React, { useState, useEffect, useCallback } from "react";

const API_URL = "https://pro-erp-system.stacknet.lk/items/api/public";
const PLACEHOLDER = "https://placehold.co/800x800/1a1a1a/ef4444?text=No+Image";
const WHATSAPP_NUMBER = "+94765794062";

// ─── Cart Context (inline) ───────────────────────────────────────────────────
function useCart() {
  const [cart, setCart] = useState({});

  const addToCart = useCallback((product) => {
    setCart((prev) => ({
      ...prev,
      [product.id]: { ...product, qty: (prev[product.id]?.qty || 0) + 1 },
    }));
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    if (qty < 1) return;
    setCart((prev) =>
      prev[id] ? { ...prev, [id]: { ...prev[id], qty } } : prev
    );
  }, []);

  const clearCart = useCallback(() => setCart({}), []);

  const items = Object.values(cart);
  const total = items.reduce((s, i) => s + i.qty, 0);

  return { cart, items, total, addToCart, removeFromCart, setQty, clearCart };
}

// ─── WhatsApp message builder ─────────────────────────────────────────────────-
function buildWhatsAppMessage(items) {
  const lines = items.map((i) => `• ${i.name} × ${i.qty}`).join("\n");
  return `Hello! I'd like to order the following items:\n\n${lines}\n\nPlease confirm availability and pricing. Thank you!`;
}

// ─── Qty Stepper ─────────────────────────────────────────────────────────────
function QtyStepper({ value, onChange, small }) {
  const s = small ? 28 : 34;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, border: "1px solid #2a2a2a", borderRadius: 4, overflow: "hidden", height: s }}>
      <button
        onClick={() => onChange(value - 1)}
        style={{ width: s, height: s, background: "#181818", border: "none", color: "#9ca3af", fontSize: "1rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
        onMouseEnter={(e) => (e.target.style.background = "#ef444422")}
        onMouseLeave={(e) => (e.target.style.background = "#181818")}
      >−</button>
      <span style={{ width: small ? 28 : 36, textAlign: "center", fontFamily: "'Barlow', sans-serif", fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}>{value}</span>
      <button
        onClick={() => onChange(value + 1)}
        style={{ width: s, height: s, background: "#181818", border: "none", color: "#9ca3af", fontSize: "1rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
        onMouseEnter={(e) => (e.target.style.background = "#ef444422")}
        onMouseLeave={(e) => (e.target.style.background = "#181818")}
      >+</button>
    </div>
  );
}

// ─── Cart Drawer ──────────────────────────────────────────────────────────────
function CartDrawer({ open, onClose, items, setQty, removeFromCart, clearCart }) {
  const handleOrder = () => {
    if (!items.length) return;
    const msg = buildWhatsAppMessage(items);
    window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
          zIndex: 1100, opacity: open ? 1 : 0, pointerEvents: open ? "all" : "none", transition: "opacity 0.3s",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed", top: 0, right: 0, width: "min(420px, 100vw)", height: "100vh",
          background: "#0a0a0a", borderLeft: "1px solid #1f1f1f", zIndex: 1200,
          transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{ padding: "1.4rem 1.5rem", borderBottom: "1px solid #1a1a1a", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ background: "#ef4444", color: "#fff", fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.2em", padding: "2px 8px", borderRadius: 2, textTransform: "uppercase", fontFamily: "'Barlow', sans-serif", display: "inline-block", marginBottom: 6 }}>Order Cart</div>
            <h2 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "-0.02em" }}>
              {items.length} {items.length === 1 ? "Item" : "Items"}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #2a2a2a", color: "#9ca3af", width: 38, height: 38, borderRadius: 4, cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.5rem" }}>
          {items.length === 0 ? (
            <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#4b5563", fontFamily: "'Barlow', sans-serif", gap: 12 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              <p style={{ margin: 0, fontSize: "0.9rem" }}>Your cart is empty</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {items.map((item) => (
                <div key={item.id} style={{ display: "flex", gap: "0.75rem", background: "#111", border: "1px solid #1a1a1a", borderRadius: 6, padding: "0.75rem", alignItems: "center" }}>
                  <img
                    src={item.image || PLACEHOLDER}
                    alt={item.name}
                    onError={(e) => (e.target.src = PLACEHOLDER)}
                    style={{ width: 56, height: 56, objectFit: "contain", background: "#fff", borderRadius: 4, padding: 4, flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: "0 0 6px", fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</p>
                    <QtyStepper value={item.qty} onChange={(q) => setQty(item.id, q)} small />
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", padding: 4, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    title="Remove"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "1.2rem 1.5rem", borderTop: "1px solid #1a1a1a", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Barlow', sans-serif", fontSize: "0.8rem", color: "#6b7280", marginBottom: 4 }}>
            <span>Total items:</span>
            <span style={{ color: "#fff", fontWeight: 700 }}>{items.reduce((s, i) => s + i.qty, 0)} units</span>
          </div>

          <button
            onClick={handleOrder}
            disabled={!items.length}
            style={{
              width: "100%", padding: "14px", border: "none", borderRadius: 4, cursor: items.length ? "pointer" : "not-allowed",
              background: items.length ? "#25D366" : "#1a2a1a", color: "#fff",
              fontFamily: "'Barlow', sans-serif", fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: "opacity 0.2s",
              opacity: items.length ? 1 : 0.4,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
            Order via WhatsApp
          </button>

          {items.length > 0 && (
            <button
              onClick={clearCart}
              style={{ width: "100%", padding: "10px", background: "none", border: "1px solid #2a2a2a", color: "#6b7280", borderRadius: 4, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              Clear Cart
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────
function ProductCard({ product, onSelect, onAddToCart, inCart, qty }) {
  return (
    <div
      style={{
        background: "#111", border: `1px solid ${inCart ? "#ef444455" : "#1f1f1f"}`,
        borderRadius: 8, overflow: "hidden", cursor: "pointer",
        transition: "border-color 0.25s, transform 0.25s, box-shadow 0.25s",
        animation: "fadeUp 0.4s ease both", position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#ef4444";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(239,68,68,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = inCart ? "#ef444455" : "#1f1f1f";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Cart badge */}
      {inCart && (
        <div style={{
          position: "absolute", top: 10, right: 10, zIndex: 2,
          background: "#ef4444", color: "#fff", borderRadius: "50%",
          width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.7rem", fontWeight: 900, fontFamily: "'Barlow', sans-serif",
        }}>
          {qty}
        </div>
      )}

      <div style={{ overflow: "hidden" }} onClick={() => onSelect(product)}>
        <img
          src={product.image || PLACEHOLDER}
          alt={product.name}
          onError={(e) => (e.target.src = PLACEHOLDER)}
          style={{ width: "100%", height: 220, objectFit: "contain", background: "#fff", padding: 10, display: "block", transition: "transform 0.3s" }}
          onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
          onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
        />
      </div>

      <div style={{ padding: "12px 14px 14px" }}>
        <h3
          onClick={() => onSelect(product)}
          style={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase", margin: "0 0 10px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
        >
          {product.name}
        </h3>

        <button
          onClick={() => onAddToCart(product)}
          style={{
            width: "100%", padding: "9px 10px", background: inCart ? "#1c1010" : "#191919",
            border: `1px solid ${inCart ? "#ef4444" : "#2a2a2a"}`,
            color: inCart ? "#ef4444" : "#9ca3af",
            borderRadius: 4, cursor: "pointer",
            fontFamily: "'Barlow', sans-serif", fontSize: "0.75rem", fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#ef444422";
            e.currentTarget.style.borderColor = "#ef4444";
            e.currentTarget.style.color = "#ef4444";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = inCart ? "#1c1010" : "#191919";
            e.currentTarget.style.borderColor = inCart ? "#ef4444" : "#2a2a2a";
            e.currentTarget.style.color = inCart ? "#ef4444" : "#9ca3af";
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          {inCart ? `In Cart (${qty})` : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}

// ─── Product Modal ────────────────────────────────────────────────────────────
function ProductModal({ product, onClose, onAddToCart, cart }) {
  const [localQty, setLocalQty] = useState(1);
  const inCart = !!cart[product.id];
  const cartQty = cart[product.id]?.qty || 0;

  const handleAddMany = () => {
    for (let i = 0; i < localQty; i++) onAddToCart(product);
  };

  const handleQuickOrder = () => {
    const msg = `Hi, I'm interested in *${product.name}* × ${localQty}. Please share details.`;
    window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ background: "#0f0f0f", border: "1px solid #1f1f1f", borderRadius: 10, width: "100%", maxWidth: 480, overflow: "hidden", animation: "fadeUp 0.25s ease" }}>
        <div style={{ height: 4, background: "linear-gradient(90deg, #ef4444, #dc2626)" }} />

        <div style={{ position: "relative", height: 280, background: "#111" }}>
          <img
            src={product.image || PLACEHOLDER}
            alt={product.name}
            onError={(e) => (e.target.src = PLACEHOLDER)}
            style={{ width: "100%", height: "100%", objectFit: "contain", padding: 16 }}
          />
          <button onClick={onClose} style={{ position: "absolute", top: 12, right: 12, background: "#0f0f0fcc", border: "1px solid #2a2a2a", color: "#fff", width: 36, height: 36, borderRadius: 4, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        <div style={{ padding: "1.5rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 900, textTransform: "uppercase", margin: "0 0 0.5rem" }}>{product.name}</h2>
          <p style={{ color: "#9ca3af", fontFamily: "'Barlow', sans-serif", fontSize: "0.88rem", lineHeight: 1.7, margin: "0 0 1.5rem" }}>
            {product.description || "Premium quality product designed to fuel your performance and support your goals."}
          </p>

          {/* Qty selector */}
          <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 6, padding: "1rem", marginBottom: "1rem" }}>
            <p style={{ margin: "0 0 10px", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6b7280", fontFamily: "'Barlow', sans-serif" }}>Quantity</p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <QtyStepper value={localQty} onChange={setLocalQty} />
              {inCart && (
                <span style={{ fontSize: "0.75rem", fontFamily: "'Barlow', sans-serif", color: "#ef4444", fontWeight: 600 }}>
                  {cartQty} already in cart
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={handleAddMany}
              style={{ width: "100%", padding: 13, background: "#ef4444", border: "none", color: "#fff", borderRadius: 4, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.88rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              Add {localQty > 1 ? `${localQty} × ` : ""}to Cart
            </button>

            <button
              onClick={handleQuickOrder}
              style={{ width: "100%", padding: 13, background: "#25D36622", border: "1px solid #25D36655", color: "#25D366", borderRadius: 4, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.88rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
              Quick Order via WhatsApp
            </button>

            <button onClick={onClose} style={{ width: "100%", padding: 11, background: "none", border: "1px solid #2a2a2a", color: "#6b7280", borderRadius: 4, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Shop ────────────────────────────────────────────────────────────────
const Shop = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const { cart, items: cartItems, total: cartTotal, addToCart, removeFromCart, setQty, clearCart } = useCart();

  useEffect(() => {
    fetch(API_URL)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCategories(data.categories);
        else setError("Unable to load products.");
        setLoading(false);
      })
      .catch(() => {
        setError("Network error.");
        setLoading(false);
      });
  }, []);

  const allProducts = categories.flatMap((c) => c.products || []);
  const displayedProducts = (
    selectedCategory === "All"
      ? allProducts
      : categories.find((c) => c.name === selectedCategory)?.products || []
  ).filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={{ minHeight: "100vh", background: "#080808", color: "#fff", fontFamily: "'Barlow Condensed', 'Impact', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; background: #080808; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cartBounce {
          0%,100% { transform: scale(1); }
          40% { transform: scale(1.3); }
          70% { transform: scale(0.9); }
        }

        .shop-layout { display: flex; }

        .sidebar {
          width: 260px; flex-shrink: 0; background: #0d0d0d;
          border-right: 1px solid #1f1f1f; padding: 2rem 1.5rem;
          position: sticky; top: 0; height: 100vh; overflow-y: auto; z-index: 100;
        }

        .main-content { flex: 1; padding: 2.5rem; min-width: 0; }
        .mobile-topbar { display: none; }

        .product-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 1.5rem;
        }

        .cat-btn {
          background: none; border: 1px solid #1f1f1f; color: #9ca3af;
          padding: 10px 14px; border-radius: 4px;
          font-family: 'Barlow', sans-serif; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;
          transition: all 0.2s; text-align: left; width: 100%;
        }
        .cat-btn:hover { border-color: #ef4444; color: #ef4444; }
        .cat-btn.active { background: #ef4444; border-color: #ef4444; color: #fff; }

        .search-input {
          width: 100%; background: #111; border: 1px solid #1f1f1f;
          color: #fff; padding: 12px 14px; border-radius: 4px;
          font-family: 'Barlow', sans-serif; font-size: 0.9rem; outline: none;
          transition: border-color 0.2s;
        }
        .search-input::placeholder { color: #4b5563; }
        .search-input:focus { border-color: #ef4444; }

        .cart-fab {
          position: fixed; bottom: 24px; right: 24px; z-index: 900;
          background: #ef4444; border: none; color: #fff; width: 58px; height: 58px;
          border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 24px rgba(239,68,68,0.45); transition: transform 0.2s, box-shadow 0.2s;
        }
        .cart-fab:hover { transform: scale(1.08); box-shadow: 0 6px 32px rgba(239,68,68,0.6); }

        .cart-fab-badge {
          position: absolute; top: -4px; right: -4px;
          background: #fff; color: #ef4444;
          border-radius: 50%; width: 22px; height: 22px;
          font-family: 'Barlow', sans-serif; font-size: 0.7rem; font-weight: 900;
          display: flex; align-items: center; justify-content: center;
        }

        @media (max-width: 900px) {
          .shop-layout { flex-direction: column; }
          .mobile-topbar {
            display: flex; align-items: center; justify-content: space-between;
            padding: 1rem; border-bottom: 1px solid #1f1f1f;
            background: #0d0d0d; position: sticky; top: 0; z-index: 200;
          }
          .sidebar {
            position: fixed; left: -100%; top: 0; width: 280px;
            height: 100vh; transition: left 0.3s ease; z-index: 999;
          }
          .sidebar.open { left: 0; }
          .main-content { padding: 1rem; }
          .product-grid { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        }

        @media (max-width: 500px) {
          .product-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* MOBILE TOPBAR */}
      <div className="mobile-topbar">
        <h2 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 900, textTransform: "uppercase" }}>Shop</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setCartOpen(true)}
            style={{ position: "relative", background: "#ef4444", border: "none", color: "#fff", padding: "10px 14px", borderRadius: 4, cursor: "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            {cartTotal > 0 && <span>{cartTotal}</span>}
          </button>
          <button
            onClick={() => setMobileSidebarOpen(true)}
            style={{ background: "#191919", border: "1px solid #2a2a2a", color: "#fff", padding: "10px 14px", borderRadius: 4, cursor: "pointer", fontWeight: 700 }}
          >
            ☰
          </button>
        </div>
      </div>

      <div className="shop-layout">
        {/* SIDEBAR */}
        <aside className={`sidebar${mobileSidebarOpen ? " open" : ""}`}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
            <button onClick={() => setMobileSidebarOpen(false)} style={{ background: "none", border: "1px solid #1f1f1f", color: "#fff", padding: "8px 12px", cursor: "pointer", borderRadius: 4 }}>✕</button>
          </div>

          <div style={{ display: "inline-block", background: "#ef4444", color: "#fff", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.2em", padding: "3px 10px", borderRadius: 2, textTransform: "uppercase", marginBottom: "1.5rem", fontFamily: "'Barlow', sans-serif" }}>Browse</div>

          <h1 style={{ fontSize: "2rem", fontWeight: 900, textTransform: "uppercase", marginBottom: "2rem", lineHeight: 1 }}>Shop</h1>

          <div style={{ marginBottom: "2rem" }}>
            <p style={{ color: "#4b5563", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "'Barlow', sans-serif", marginBottom: "0.6rem", margin: "0 0 0.6rem" }}>Search</p>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div>
            <p style={{ color: "#4b5563", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "'Barlow', sans-serif", margin: "0 0 0.6rem" }}>Categories</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className={`cat-btn${selectedCategory === "All" ? " active" : ""}`} onClick={() => { setSelectedCategory("All"); setMobileSidebarOpen(false); }}>All Products</button>
              {categories.map((cat) => (
                <button key={cat.name} className={`cat-btn${selectedCategory === cat.name ? " active" : ""}`} onClick={() => { setSelectedCategory(cat.name); setMobileSidebarOpen(false); }}>{cat.name}</button>
              ))}
            </div>
          </div>

          {/* Cart summary in sidebar (desktop) */}
          {cartTotal > 0 && (
            <div style={{ marginTop: "2rem", background: "#111", border: "1px solid #1a1a1a", borderRadius: 6, padding: "1rem" }}>
              <p style={{ margin: "0 0 10px", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6b7280", fontFamily: "'Barlow', sans-serif" }}>Cart</p>
              <p style={{ margin: "0 0 12px", fontFamily: "'Barlow', sans-serif", fontSize: "0.9rem", color: "#fff" }}>
                <span style={{ fontWeight: 700, fontSize: "1.3rem" }}>{cartTotal}</span> <span style={{ color: "#6b7280" }}>item{cartTotal !== 1 ? "s" : ""}</span>
              </p>
              <button onClick={() => setCartOpen(true)} style={{ width: "100%", padding: "10px", background: "#ef4444", border: "none", color: "#fff", borderRadius: 4, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                View Cart →
              </button>
            </div>
          )}
        </aside>

        {/* MAIN */}
        <main className="main-content">
          {loading && <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: "#4b5563", fontFamily: "'Barlow', sans-serif" }}>Loading products...</div>}
          {error && <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontFamily: "'Barlow', sans-serif" }}>{error}</div>}

          {!loading && !error && (
            <>
              <div style={{ marginBottom: "2rem", display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                  <div style={{ width: 40, height: 4, background: "#ef4444", marginBottom: "1rem" }} />
                  <h2 style={{ fontSize: "clamp(1.8rem, 5vw, 2.8rem)", fontWeight: 900, textTransform: "uppercase", lineHeight: 1, margin: "0 0 0.4rem" }}>
                    {selectedCategory === "All" ? "All Products" : selectedCategory}
                  </h2>
                  <p style={{ color: "#4b5563", fontFamily: "'Barlow', sans-serif", fontSize: "0.9rem", margin: 0 }}>
                    {displayedProducts.length} products available
                  </p>
                </div>

                {cartTotal > 0 && (
                  <button
                    onClick={() => setCartOpen(true)}
                    style={{ display: "flex", alignItems: "center", gap: 10, background: "#111", border: "1px solid #ef444455", color: "#ef4444", padding: "10px 16px", borderRadius: 6, cursor: "pointer", fontFamily: "'Barlow', sans-serif", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                    {cartTotal} item{cartTotal !== 1 ? "s" : ""} in cart — View Order
                    <span style={{ color: "#4b5563" }}>→</span>
                  </button>
                )}
              </div>

              <div className="product-grid">
                {displayedProducts.length > 0 ? (
                  displayedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onSelect={setSelectedProduct}
                      onAddToCart={addToCart}
                      inCart={!!cart[product.id]}
                      qty={cart[product.id]?.qty || 0}
                    />
                  ))
                ) : (
                  <p style={{ gridColumn: "1/-1", textAlign: "center", color: "#4b5563", fontFamily: "'Barlow', sans-serif", padding: "4rem 0" }}>No products found</p>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* FLOATING CART BUTTON */}
      {cartTotal > 0 && (
        <button className="cart-fab" onClick={() => setCartOpen(true)} title="View Cart">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <span className="cart-fab-badge" style={{ animation: "cartBounce 0.4s ease" }}>{cartTotal}</span>
        </button>
      )}

      {/* CART DRAWER */}
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        items={cartItems}
        setQty={setQty}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
      />

      {/* PRODUCT MODAL */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={addToCart}
          cart={cart}
        />
      )}
    </div>
  );
};

export default Shop;